import React from 'react';
import { Shield } from 'lucide-react';

const CompliancePolicy = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] mb-4">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            AML / KYC Policy
          </h1>
          <p className="text-sm text-[#5B6B7F]">
            Last updated: 2025-11-18
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* 1. Company Information & Regulatory Status */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">1. Company Information & Regulatory Status</h2>
            <div className="space-y-3">
              <p className="text-[#5B6B7F] leading-relaxed">
                <span className="font-semibold">Company name:</span><br />
                COINEXUS Spółka z ograniczoną odpowiedzialnością ("COINEXUS Sp. z o.o.")
              </p>
              <p className="text-[#5B6B7F] leading-relaxed">
                <span className="font-semibold">Legal form:</span><br />
                Limited Liability Company
              </p>
              <p className="text-[#5B6B7F] leading-relaxed">
                <span className="font-semibold">KRS number:</span> 0001142506<br />
                <span className="font-semibold">NIP (Tax ID):</span> 7252350358<br />
                <span className="font-semibold">REGON:</span> 540348512
              </p>
              <p className="text-[#5B6B7F] leading-relaxed">
                <span className="font-semibold">Registered office:</span><br />
                Juliana Tuwima 48, unit 11,<br />
                90-021 Łódź, Poland
              </p>
            </div>
          </div>

          {/* 1.1 Company Details */}
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-[#1E3A5F]">1.1 Company Details</h3>
            <p className="text-[#5B6B7F] leading-relaxed">
              COINEXUS Sp. z o.o. is a regulated Virtual Asset Service Provider (VASP) company, incorporated under the laws of the Republic of Poland with Company number 0001142506.
            </p>
          </div>

          {/* 1.2 Regulated Status */}
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-[#1E3A5F]">1.2 Regulated Status</h3>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company is registered as a Virtual Asset Service Provider (VASP) in Poland.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              The VASP license/registration number is RDWW-1750.
            </p>
          </div>

          {/* 1.3 Supervisory Authority (FIU – GIIF / GIFI) */}
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-[#1E3A5F]">1.3 Supervisory Authority (FIU – GIIF / GIFI)</h3>
            <p className="text-[#5B6B7F] leading-relaxed">
              As a VASP in Poland, the Company is supervised by the Polish Financial Intelligence Unit:
            </p>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
              <p className="text-[#5B6B7F] leading-relaxed font-semibold">
                Generalny Inspektor Informacji Finansowej ("GIIF")<br />
                (EN: General Inspector of Financial Information "GIFI")
              </p>
              <p className="text-[#5B6B7F] leading-relaxed mt-3">
                <span className="font-semibold">Address:</span> Świętokrzyska 12, 00-916 Warsaw, Poland<br />
                <span className="font-semibold">Phone:</span> +48 (22) 694-59-70, +48 (22) 694-30-60<br />
                <span className="font-semibold">Email:</span> sekretariat.IF@mf.gov.pl
              </p>
            </div>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              GIIF is Poland's Financial Intelligence Unit ("FIU") and the primary authority responsible for AML/CFT supervision and enforcement.
            </p>
          </div>

          {/* 1.4 Identification (KYC) Policy */}
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-[#1E3A5F]">1.4 Identification (KYC) Policy</h3>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company applies Know Your Customer (KYC) procedures to all clients before any transaction is processed.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              The following steps are required:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Collection of valid government-issued ID (passport or national ID).</li>
              <li>Proof of address not older than 3 months (e.g., utility bill or bank statement).</li>
              <li>Screening against sanctions and PEP lists via Allpass.ai.</li>
              <li>Verification of Source of Wealth when applicable.</li>
            </ol>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              No transactions are allowed before successful verification.
            </p>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-[#5B6B7F]">
                <span className="font-semibold text-[#1E3A5F]">Authorized by:</span> Mohamed Oussema Sassi
              </p>
              <p className="text-[#5B6B7F]">
                <span className="font-semibold text-[#1E3A5F]">Position:</span> Co-Founder, Executive Director & MLRO
              </p>
            </div>
          </div>

          {/* 2. Purpose of the Policy */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">2. Purpose of the Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              This Policy outlines the principles and procedures applied by COINEXUS Sp. z o.o. ("Coinexus", the "Company") to prevent money laundering, terrorist financing, fraud, and other illegal financial activity.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              It ensures that the Company:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Complies with applicable AML/CFT laws and regulations;</li>
              <li>Conducts adequate customer due diligence;</li>
              <li>Monitors transactions and client activity;</li>
              <li>Reports suspicious activity to GIIF and other competent authorities, where required.</li>
            </ul>
          </div>

          {/* 3. Scope */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">3. Scope</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              This Policy applies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>All Coinexus clients;</li>
              <li>All OTC settlement transactions (crypto-to-fiat and fiat-to-crypto);</li>
              <li>All employees involved in onboarding, compliance, risk, operations, or client servicing;</li>
              <li>Third-party service providers engaged in KYC/AML or screening activities on behalf of the Company.</li>
            </ul>
          </div>

          {/* 4. Regulatory Framework */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">4. Regulatory Framework</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus complies with, among others:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>EU Anti-Money Laundering Directives (including 5AMLD and 6AMLD);</li>
              <li>The Polish AML Act and related regulations;</li>
              <li>Requirements and guidelines issued by GIIF;</li>
              <li>International sanctions regimes (UN, EU, OFAC and others, where relevant).</li>
            </ul>
          </div>

          {/* 5. Client Due Diligence (CDD) */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">5. Client Due Diligence (CDD)</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company applies a risk-based approach to Client Due Diligence. This means the level of checks and monitoring depends on the client's risk profile.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              CDD includes, at minimum:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Identification and verification of the client's identity;</li>
              <li>Identification and verification of beneficial owners (where applicable);</li>
              <li>Sanctions and PEP screening;</li>
              <li>Assessment of the client's risk profile;</li>
              <li>Ongoing monitoring of the business relationship.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Enhanced Due Diligence (EDD) measures are applied to high-risk clients, high-risk jurisdictions, or high-risk products/services.
            </p>
          </div>

          {/* 6. Risk-Based Approach (RBA) */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">6. Risk-Based Approach (RBA)</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company evaluates the risk of money laundering and terrorist financing based on:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Jurisdiction and geographic risk;</li>
              <li>Client type (e.g., corporate, professional, individual, high net-worth);</li>
              <li>Nature and purpose of the business relationship;</li>
              <li>Expected transaction volume and frequency;</li>
              <li>Source of funds and Source of Wealth;</li>
              <li>Sanctions and PEP exposure.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Clients and activities assessed as high-risk are subject to stricter CDD/EDD, enhanced monitoring and, where appropriate, restrictions or refusal of service.
            </p>
          </div>

          {/* 7. Prohibited Activities */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">7. Prohibited Activities</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company strictly prohibits the use of its services for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Money laundering or terrorist financing;</li>
              <li>Fraud, tax evasion or other financial crimes;</li>
              <li>Activities linked to the darknet or illicit marketplaces;</li>
              <li>Use of mixing, tumbling, or other anonymity-enhancing services for funds;</li>
              <li>Unlicensed online gambling activities;</li>
              <li>Shell-banking and fictitious or fraudulent identities;</li>
              <li>Third-party beneficiary payments that do not match the verified client;</li>
              <li>Any activity in breach of EU, Polish, or international regulations.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Suspicious transactions and activities are reviewed and, where required, reported to GIIF or other competent authorities.
            </p>
          </div>

          {/* 8. Transaction Rules */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">8. Transaction Rules</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              To reduce the risk of misuse of its services, Coinexus applies the following transactional rules:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>All fiat transfers must be executed to and from bank accounts held in the client's own name;</li>
              <li>The Company does not accept or process cash;</li>
              <li>The Company does not provide custody or wallet services for clients' funds;</li>
              <li>The Company does not operate a trading platform or offer leveraged or speculative products;</li>
              <li>Transactions that do not align with the documented client profile or Source of Wealth may be delayed, reviewed, or rejected.</li>
            </ul>
          </div>

          {/* 9. Data Protection & Record-Keeping */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">9. Data Protection & Record-Keeping</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company maintains all AML/KYC records in accordance with applicable laws, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Retaining KYC and transaction data for at least 5 years from the end of the business relationship or from the date of an occasional transaction, as required by law;</li>
              <li>Ensuring that personal data is processed in line with GDPR and relevant privacy regulations;</li>
              <li>Deleting or anonymizing data securely once retention periods expire, unless further retention is legally justified;</li>
              <li>Restricting access to AML/KYC data to authorized personnel only.</li>
            </ul>
          </div>

          {/* 10. Sanctions & PEP Screening */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">10. Sanctions & PEP Screening</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              All clients and, where applicable, beneficial owners are screened against:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>United Nations sanctions lists;</li>
              <li>European Union sanctions lists;</li>
              <li>OFAC and other relevant sanctions lists;</li>
              <li>Politically Exposed Person (PEP) databases.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              The Company uses Allpass.ai and other specialized tools to perform sanctions and PEP screening both at onboarding and on a periodic/ongoing basis for active clients.
            </p>
          </div>

          {/* 11. Reporting Obligations */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">11. Reporting Obligations</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company is committed to fulfilling its reporting obligations, which may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Filing Suspicious Activity Reports (SARs) or equivalent reports with GIIF;</li>
              <li>Reporting certain threshold or unusual transactions, where required;</li>
              <li>Cooperating with law enforcement agencies and competent authorities;</li>
              <li>Providing relevant documentation and information to regulators upon lawful request.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Coinexus does not inform clients when a SAR or similar report has been filed, where such disclosure would be prohibited ("tipping-off" rules).
            </p>
          </div>

          {/* 12. Training & Internal Controls */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">12. Training & Internal Controls</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Company ensures that employees involved in AML/CTF-relevant functions receive regular training on:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>AML/CFT legal and regulatory requirements;</li>
              <li>Internal AML/CTF procedures and guidelines;</li>
              <li>Identification of red flags and suspicious behavior;</li>
              <li>Sanctions compliance and screening procedures;</li>
              <li>Information security and confidentiality.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Internal controls and procedures are reviewed periodically to maintain effectiveness and alignment with regulatory expectations.
            </p>
          </div>

          {/* 13. Independent Audits */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">13. Independent Audits</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              AML/CTF measures and internal controls may be subject to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Internal audits;</li>
              <li>External audits or independent compliance assessments;</li>
              <li>Reviews or inspections carried out by GIIF or other competent authorities.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Where deficiencies are identified, the Company implements corrective measures within a reasonable timeframe.
            </p>
          </div>

          {/* 14. Policy Updates */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">14. Policy Updates</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              This Policy may be updated to reflect:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Changes in applicable laws and regulations;</li>
              <li>New or updated guidance from GIIF or other regulators;</li>
              <li>Internal risk assessments and business model changes;</li>
              <li>Industry best practices in AML/CFT.</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              The current version of this Policy is made available on the Company's website.
            </p>
          </div>

          {/* 15. Contact Information */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">15. Contact Information</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              For any questions or concerns related to this AML/KYC Policy or AML/CTF matters, please contact:
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              <span className="font-semibold">Email:</span> <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a>
            </p>
          </div>

          {/* End of Policy */}
          <div className="pt-8 border-t-2 border-gray-300">
            <p className="text-center text-[#5B6B7F] font-semibold italic">
              End of Policy
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default CompliancePolicy;
